import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../utils/api';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardContent } from './ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Plus, Search, Edit, Trash2, BookOpen, Eye } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import BranchLabel from './ui/BranchLabel';
import useBranchLabels from '../hooks/useBranchLabels';

const SUBJECT_TYPES = [
  { value: 'theory', label: 'Theory' },
  { value: 'practical', label: 'Practical' },
  { value: 'both', label: 'Both' },
];

const emptyForm = () => ({
  name: '',
  code: '',
  type: 'theory',
  description: '',
  status: 'active',
});

const statusBadgeClass = (status) => (
  status === 'active'
    ? 'bg-green-500/20 text-green-400'
    : 'bg-muted text-muted-foreground'
);

const Subjects = () => {
  const { user, canManageFeature } = useAuth();
  const canManage = canManageFeature('subjects');
  const { toast } = useToast();
  const { showBranchLabels } = useBranchLabels();

  const [subjects, setSubjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingSubject, setEditingSubject] = useState(null);
  const [formData, setFormData] = useState(emptyForm());

  useEffect(() => {
    fetchSubjects();
  }, [user?.school_id]);

  const fetchSubjects = async () => {
    try {
      const params = {
        include_inactive: 1,
        ...(user?.school_id ? { school_id: user.school_id } : {}),
      };
      const response = await api.get('/subjects', { params });
      setSubjects(response.data.data || []);
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.message || 'Failed to fetch subjects',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        name: formData.name.trim(),
        code: formData.code.trim() || null,
        type: formData.type || 'theory',
        description: formData.description.trim() || null,
        status: formData.status,
        ...(user?.school_id ? { school_id: user.school_id } : {}),
      };

      if (editingSubject) {
        await api.put(`/subjects/${editingSubject.id}`, payload);
        toast({ title: 'Success', description: 'Subject updated successfully' });
      } else {
        await api.post('/subjects', payload);
        toast({ title: 'Success', description: 'Subject created successfully' });
      }
      setIsDialogOpen(false);
      resetForm();
      fetchSubjects();
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.message || 'Failed to save subject',
        variant: 'destructive',
      });
    }
  };

  const handleEdit = (subject) => {
    setEditingSubject(subject);
    setFormData({
      name: subject.name || '',
      code: subject.code || '',
      type: subject.type || 'theory',
      description: subject.description || '',
      status: subject.status || 'active',
    });
    setIsDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this subject?')) return;
    try {
      await api.delete(`/subjects/${id}`);
      toast({ title: 'Success', description: 'Subject deleted' });
      fetchSubjects();
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.message || 'Failed to delete subject',
        variant: 'destructive',
      });
    }
  };

  const resetForm = () => {
    setFormData(emptyForm());
    setEditingSubject(null);
  };

  const filtered = subjects.filter((s) => {
    const q = search.toLowerCase();
    return `${s.name} ${s.code || ''} ${s.description || ''}`.toLowerCase().includes(q);
  });

  return (
    <div className="space-y-6" data-testid="subjects-page">
      <div className="flex flex-col sm:flex-row justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Subjects</h1>
          <p className="text-muted-foreground">Manage subjects and class assignments</p>
        </div>
        {canManage && (
          <Dialog
            open={isDialogOpen}
            onOpenChange={(open) => {
              setIsDialogOpen(open);
              if (!open) resetForm();
            }}
          >
            <DialogTrigger asChild>
              <Button className="w-full sm:w-auto bg-blue-600 hover:bg-blue-700" data-testid="add-subject-btn">
                <Plus className="w-4 h-4 mr-2" /> Add Subject
              </Button>
            </DialogTrigger>
            <DialogContent className="border-border text-foreground max-w-lg max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>{editingSubject ? 'Edit Subject' : 'Add Subject'}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label>Subject Name *</Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="bg-muted border-border"
                    required
                  />
                </div>
                <div>
                  <Label>Subject Code</Label>
                  <Input
                    value={formData.code}
                    onChange={(e) => setFormData({ ...formData, code: e.target.value })}
                    className="bg-muted border-border"
                    placeholder="Optional"
                  />
                </div>
                <div>
                  <Label>Type</Label>
                  <Select value={formData.type} onValueChange={(v) => setFormData({ ...formData, type: v })}>
                    <SelectTrigger className="bg-muted border-border"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-muted border-border">
                      {SUBJECT_TYPES.map((t) => (
                        <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Description</Label>
                  <Input
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    className="bg-muted border-border"
                    placeholder="Optional"
                  />
                </div>
                <div>
                  <Label>Status</Label>
                  <Select value={formData.status} onValueChange={(v) => setFormData({ ...formData, status: v })}>
                    <SelectTrigger className="bg-muted border-border"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-muted border-border">
                      <SelectItem value="active">Active</SelectItem>
                      <SelectItem value="inactive">Inactive</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <Button type="submit" className="w-full bg-blue-600 hover:bg-blue-700">
                  {editingSubject ? 'Update' : 'Create'}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      <Card className="border-border">
        <CardContent className="p-4">
          <div className="relative">
            <Search className="absolute left-3 top-3 w-4 h-4 text-muted-foreground" />
            <Input
              type="search"
              placeholder="Search subjects..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10 bg-muted border-border text-foreground"
            />
          </div>
        </CardContent>
      </Card>

      {loading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500" />
        </div>
      ) : filtered.length === 0 ? (
        <Card className="border-border">
          <CardContent className="py-12 text-center">
            <BookOpen className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
            <p className="text-muted-foreground">No subjects found</p>
          </CardContent>
        </Card>
      ) : (
        <Card className="border-border overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border bg-muted/40 text-left text-muted-foreground">
                  <th className="px-4 py-3 font-medium">Subject Name</th>
                  <th className="px-4 py-3 font-medium">Code</th>
                  <th className="px-4 py-3 font-medium">Classes</th>
                  <th className="px-4 py-3 font-medium">Status</th>
                  <th className="px-4 py-3 font-medium text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((subject) => (
                  <tr key={subject.id} className="border-b border-border last:border-0 hover:bg-muted/20">
                    <td className="px-4 py-3">
                      <Link to={`/subjects/${subject.id}`} className="font-medium text-foreground hover:text-blue-400 inline-flex items-center gap-2 flex-wrap">
                        {subject.name}
                        <BranchLabel name={subject.branch_name} show={showBranchLabels} />
                      </Link>
                    </td>
                    <td className="px-4 py-3 text-muted-foreground">{subject.code || '—'}</td>
                    <td className="px-4 py-3 text-muted-foreground">{subject.assigned_classes_count ?? 0}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex px-2 py-0.5 rounded text-xs font-medium capitalize ${statusBadgeClass(subject.status)}`}>
                        {subject.status || 'active'}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-1">
                        <Button variant="ghost" size="icon" asChild className="text-muted-foreground hover:text-foreground">
                          <Link to={`/subjects/${subject.id}`}><Eye className="w-4 h-4" /></Link>
                        </Button>
                        {canManage && (
                          <>
                            <Button variant="ghost" size="icon" onClick={() => handleEdit(subject)} className="text-muted-foreground hover:text-foreground">
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button variant="ghost" size="icon" onClick={() => handleDelete(subject.id)} className="text-muted-foreground hover:text-red-400">
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  );
};

export default Subjects;
